import Text from 'components/Text/Text';
import React, { useContext, useEffect } from 'react';
import { IEditModifierContext, EditModifierContext } from '../../../../editModifierState/editModifierContext';
import { getFileIdForSubmit, preventDefaultForEnterKey } from 'utils/helpers';
import { Form } from 'antd';
import Button from 'components/Button/Button';
import { CustomerSelect } from './components/CustomerSelect';
import { NameModifierInput } from './components/NameModifierInput';
import { PosNameInput } from './components/PosNameInput';
import { ArticleNumberInput } from './components/ArticleNumberInput';
import { DescriptionField } from './components/DescriptionField';
import { ExternalIdField } from './components/ExternalIdField';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import { HasSchedulePriorityCheckBox } from './components/HasSchedulePriorityCheckBox';
import { ActivationScheduleInput } from './components/ActivationScheduleInput';
import { DeactivationScheduleInput } from './components/DeactivationScheduleInput';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import { RestaurantsMultipleSelect } from './components/RestaurantsMultipleSelect';
import { BtnBlock } from './components/BtnBlock';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './InputFields.module.scss';

export const InputFields: React.FC = () => {
	const {
		changeLoaderStatus,
		changeTypeOfModalWindow,
		setDataForSubmit,
		changeNameOfModifier,
		changeDescription,
		setFetchedCustomers,
		setIDCustomer,
		changeExternalId,
		generateUniqueExternalId,
		setDefaultCustomerLanguage,
		setTranslationTab,
		changeNameTranslation,
		changeDescriptionTranslation,
		toggleHasSchedulePriority,
		changeActivationSchedule,
		changeDeactivationSchedule,
		setIDsRestaurant,
		setFetchedRestaurants,
		modifierID,
		fileId,
		filePath,
		nameOfModifier,
		description,
		initialIDSelectedCustomer,
		IDSelectedCustomer,
		posName,
		externalId,
		articleNumber,
		selectedTranslationTab,
		translations,
		defaultCustomerLanguage,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		initialIDsSelectedRestaurant,
		IDsSelectedRestaurant,
	} = useContext<IEditModifierContext>(EditModifierContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			nameOfModifier,
			IDSelectedCustomer,
			description,
			posName,
			externalId,
			articleNumber,
			hasSchedulePriority,
			activationSchedule,
			deactivationSchedule,
			IDsSelectedRestaurant,
		});
	}, [
		nameOfModifier,
		IDSelectedCustomer,
		description,
		posName,
		externalId,
		articleNumber,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		IDsSelectedRestaurant,
	]);

	const submit = async () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		setDataForSubmit({
			id: modifierID,
			name: nameOfModifier,
			description: description || null,
			externalId: externalId || null,
			fileId: getFileIdForSubmit({ fileId, filePath }),
			overrideScheduleAvailability: hasSchedulePriority,
			activationCron: activationSchedule || null,
			deactivationCron: deactivationSchedule || null,
			translations,
			restaurantIds: IDsSelectedRestaurant,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<div className={style.infoWrapper}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#circleInfoTransparent`} />
						</svg>
						<Text
							translationNameSpace="form"
							translationKey="warningBeforeModifierChange"
							theme="secondary"
							size="s"
							display="inline"
						/>
					</div>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<NameModifierInput changeNameOfModifier={changeNameOfModifier} />
					<ArticleNumberInput />
					<PosNameInput />
					<DescriptionField changeDescription={changeDescription} />
					<CustomerSelect
						setIDCustomer={setIDCustomer}
						setFetchedCustomers={setFetchedCustomers}
						initialIDSelectedCustomer={initialIDSelectedCustomer}
					/>
					<div className={style.row}>
						<ExternalIdField changeExternalId={changeExternalId} />
						<Button onClick={generateUniqueExternalId} theme="primary">
							<Text
								translationNameSpace="form"
								translationKey="generateUniqueExternalId"
								asFragment={true}
							/>
						</Button>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="translationsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<MenuTranslationForm
						className={style.translationForm}
						setDefaultCustomerLanguage={setDefaultCustomerLanguage}
						setTranslationTab={setTranslationTab}
						changeNameTranslation={changeNameTranslation}
						changeDescriptionTranslation={changeDescriptionTranslation}
						isShowLanguageName={false}
						IDSelectedCustomer={IDSelectedCustomer}
						translations={translations}
						selectedTranslationTab={selectedTranslationTab}
						nameLabel="nameModifierLabel"
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<HasSchedulePriorityCheckBox
						toggleHasSchedulePriority={toggleHasSchedulePriority}
						hasSchedulePriority={hasSchedulePriority}
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantListHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<RestaurantsMultipleSelect
						setIDsRestaurant={setIDsRestaurant}
						setFetchedRestaurants={setFetchedRestaurants}
						changeLoaderStatus={changeLoaderStatus}
						IDSelectedCustomer={IDSelectedCustomer}
						initialIDsSelectedRestaurant={initialIDsSelectedRestaurant}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};
